import isEmpty from 'lodash/isEmpty';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PageError from 'pages/404';
import DynamicAds, { KEY_ADS_POSITION } from 'src/components/ads/dynamic-ads';
import CardV from 'src/components/cards-video/card-v';
import Layout from 'src/components/layouts';
import SecFullH from 'src/components/sections/section-video/sec-full-h';
import { APP_URL, IS_NO_ROBOT_INDEX, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { escapeChar, isExistAds } from 'src/utils/helper';
import { GET } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';

const PageVideoCategory = ({ articles, category, subCategory, ads }) => {
  const { user } = useUser();
  const sessionSevId = user ? decryptionClient(user) : null;
  if (isEmpty(articles)) return <PageError sessionSevId={sessionSevId} />;
  const defaultCategory = subCategory.en ? subCategory : category;
  // console.log('PageVideoCategory', articles);
  const router = useRouter();

  let setItemListElement = '';
  const baseUrlCategory = `${APP_URL}${router.asPath}`;
  const articleDescriptionSeo = escapeChar(articles?.description) || WEB_DESC;

  if (subCategory.en != null) {
    // baseUrlCategory = `${APP_URL}/category/${category?.en}/${subCategory?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "${category?.th}",
          "item": "${APP_URL}/live/${category?.en}"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "${subCategory?.th}",
          "item": "${baseUrlCategory}"
        }
      ]`;
  } else {
    // baseUrlCategory = `${APP_URL}/category/${category?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "${category?.th}",
            "item": "${APP_URL}/live/${category?.en}"
          }
        ]`;
  }
  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={articles?.title ? `ขอบสนาม - ${articles?.title}` : WEB_TITLE}
        description={articles?.description || WEB_DESC}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          site_name: WEB_NAME,
          title: articles?.title ? `ขอบสนาม - ${articles?.title}` : WEB_TITLE,
          description: articles?.description || WEB_DESC,
          url: baseUrlCategory,
          images: [
            {
              url: IMAGE_DEFAULT,
              width: '1920',
              height: '1080',
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: articles?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={articles?.title ? `ขอบสนาม - ${articles?.title}` : WEB_TITLE} />
        <meta property='twitter:description' content={articles?.description || WEB_DESC} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="${subCategory?.en ? subCategory.en : category?.en}";
              `
          }}
        />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "https://schema.org",
                  "@type": "WebSite",
                  "name": "${WEB_NAME}",
                  "url": "${WEB_URL.WEB}",
                  "description": "${articleDescriptionSeo}",
                  "sameAs": [
                    "${WEB_URL.FACEBOOK}",
                    "${WEB_URL.YOUTUBE}",
                    "${WEB_URL.INSTAGRAM}"
                  ],
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": "${WEB_URL.WEB}search?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                  }
                }
              `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": "${subCategory?.th ? subCategory.th : category?.th}",
                    "description": "${articleDescriptionSeo}",
                    "publisher": {
                      "@type": "ProfilePage",
                      "name": "${WEB_NAME}"
                    }
                  }
                `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": ${setItemListElement}
                }
              `
          }}
        />
      </Head>
      {/* -------------------------------------------------------------------------- */}
      <Layout sessionSevId={sessionSevId}>
        {isExistAds(ads, KEY_ADS_POSITION.BILLBOARD) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}
        <CategoryList className='container mb-5' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : '' }}>
          {/* ----------------------------- Breadcrumb -------------------------- */}
          <div className='row'>
            <div className='col-12'>
              <ul className='breadcrumb'>
                <li>
                  <a
                    href='/'
                    //! DATALAYER
                    // onClick={() =>
                    //   sendDataLayerGlobal({
                    //     key: KEY_EVENT.TRACK_EVENT,
                    //     event: KEY_EVENT.BREADCRUMB,
                    //     data: {
                    //       title: 'หน้าแรก',
                    //       link: '/'
                    //     }
                    //   })
                    // }
                  >
                    หน้าแรก
                  </a>
                </li>
                <li>
                  <a
                    href={`/live/${defaultCategory.en}`}
                    //! DATALAYER
                    // onClick={() =>
                    //   sendDataLayerGlobal({
                    //     key: KEY_EVENT.TRACK_EVENT,
                    //     event: KEY_EVENT.BREADCRUMB,
                    //     data: {
                    //       title: defaultCategory.th,
                    //       link: `/live/${defaultCategory.en}`
                    //     }
                    //   })
                    // }
                  >
                    {defaultCategory.th}
                  </a>
                </li>
              </ul>
              {/* {defaultCategory.th && <h1 className='heading-title'>{defaultCategory.th}</h1>} */}
            </div>
          </div>

          <div className='row '>
            <SecFullH data={articles.slice(0, 5)} />
          </div>

          <div className='row mt-4 card-category'>
            {articles.slice(5).map((item, index) => (
              <div key={index} className='col-6 col-md-4 col-lg-3 card-v' data-aos='fade-up'>
                <CardV
                  data={item}
                  path={`/live/${item.section.en}/${item.id}`}
                  //! DATALAYER
                  // eventDataLayer={{
                  //   key: KEY_EVENT.TRACK_EVENT,
                  //   event: KEY_EVENT.CARD_CONTENT,
                  //   data: { title: item?.data?.title }
                  // }}
                />
              </div>
            ))}
          </div>
        </CategoryList>
        {isExistAds(ads, KEY_ADS_POSITION.POSTARTICLE) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
        {isExistAds(ads, KEY_ADS_POSITION.AD1X1) && isEmpty(subCategory?.th) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
      </Layout>
    </>
  );
};

const CategoryList = styled.div`
  padding-bottom: 30px;
  ul {
    list-style: none;
  }
  @media (max-width: 540px) {
    /* padding: 0; */
    .sec-full-h {
      padding: 0;
    }
    .heading-title {
      text-align: center;
    }

    .heading-title {
      padding-bottom: 5px;
      border-bottom: 1px solid #ddd;
    }
  }

  .heading-title {
    margin: 10px auto 25px auto;
  }

  .card-v {
    margin-top: 20px;
  }
`;

export async function getServerSideProps({ req, res, query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    articles: null,
    category: null,
    subCategory: null,
    sessionSevId: {},
    meta: null,
    ads: null
  };
  initProps.queryCategory = query;
  let uri = `categories/${query?.cid}`;
  if (query?.sub) {
    uri = `categories/${query?.cid}/${query?.sub}`;
  }

  try {
    const _uri = `/api/v1.0/video/${uri}`;
    const resData = await GET(_uri, false, true);
    if (isEmpty(resData)) initProps.articles = null;
    if (!isEmpty(resData)) {
      initProps.articles = resData.data;
      initProps.category = resData.category;
      initProps.subCategory = resData.subCategory;
      initProps.lastNews = resData.last_news;
      initProps.meta = resData.meta;
      initProps.ads = resData.ads;
    }
  } catch (err) {
    console.log('==========> CATEGORY ERROR', err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return {
    props: initProps
  };
}
export default PageVideoCategory;
